import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";

import { listShiftRatings, rateVenue, rateWorker } from "@/features/ratings/api/ratingsApi";
import { StarInput } from "@/features/ratings/ui/StarInput";
import type { RatingDirection } from "@/features/ratings/types";
import { Button, Textarea } from "@/shared/ui";

interface Props {
  shiftId: string;
  direction: RatingDirection;
}

export function RatingForm({ shiftId, direction }: Props) {
  const qc = useQueryClient();
  const [stars, setStars] = useState(0);
  const [reviewText, setReviewText] = useState("");

  const ratingsQuery = useQuery({
    queryKey: ["ratings", shiftId],
    queryFn: () => listShiftRatings(shiftId),
  });

  const alreadyRated = ratingsQuery.data?.some((r) => r.direction === direction);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { stars, review_text: reviewText || undefined };
      return direction === "VENUE_TO_WORKER"
        ? rateWorker(shiftId, payload)
        : rateVenue(shiftId, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ratings", shiftId] });
      setStars(0);
      setReviewText("");
    },
  });

  const targetLabel = direction === "VENUE_TO_WORKER" ? "worker" : "venue";

  if (ratingsQuery.isLoading) return null;

  if (alreadyRated) {
    const myRating = ratingsQuery.data!.find((r) => r.direction === direction)!;
    return (
      <div className="rounded-lg border border-border bg-surface p-4 shadow-card">
        <p className="text-sm font-medium text-copy">You rated the {targetLabel}</p>
        <div className="mt-1 text-warning">
          {"★".repeat(myRating.stars)}
          <span className="text-copy-subtle">{"★".repeat(5 - myRating.stars)}</span>
        </div>
        {myRating.review_text && (
          <p className="mt-2 text-sm text-copy-muted">{myRating.review_text}</p>
        )}
      </div>
    );
  }

  const err =
    mutation.error instanceof AxiosError
      ? (mutation.error.response?.data?.detail ?? "Could not submit rating")
      : null;

  return (
    <div className="space-y-3 rounded-lg border border-border bg-surface p-4 shadow-card">
      <h3 className="font-semibold tracking-display-tight text-copy">Rate the {targetLabel}</h3>
      <StarInput value={stars} onChange={setStars} />
      <Textarea
        value={reviewText}
        onChange={(e) => setReviewText(e.target.value)}
        placeholder="Optional review"
        rows={2}
      />
      {err && <p className="text-sm text-destructive">{err}</p>}
      <Button onClick={() => mutation.mutate()} disabled={stars === 0 || mutation.isPending}>
        {mutation.isPending ? "Submitting…" : "Submit rating"}
      </Button>
    </div>
  );
}
